import { FC, useEffect, useRef } from 'react'
import Head from 'next/head'
import styled, { useTheme } from 'styled-components'
import { Text } from '@/components/common'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { FitpetIcon } from '@/components/common/icons/fitpet-icon'
import SimpleModal from '@/components/common/modals/SimpleModal'
import { Skeleton } from '@/components/common/ui/skeleton'
import { LayoutHeader } from '@/components/domains/layouts'
import { FontWeightEnum } from '@/constants/font-weight.enum'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import { S3_FOLDERS_LEGACY, S3_HOST_URL } from '@/constants/legacy/constEnv'
import { LAYOUT_UNITS } from '@/constants/legacy/constLayout'
import { MembershipImageUrl } from '@/constants/membership-image-url'
import { MembershipIntroContainer } from '@/containers/membership/MembershipIntroContainer'
import { Bridge } from '@/utils/bridge/bridge'
import appBridgeProvider, { isAbleStatusBarFontChange, isAppStorageDisabled, isInAppFlag } from '@/utils/utilBridge'

const PageMembershipIntroContent: FC = () => {
  const { color } = useTheme()

  const showWarningRef = useRef<HTMLImageElement>(null)
  const {
    showWarning,
    toggleShowWarning,
    visible,
    modalOnOk,
    handleClickBottomButton,
    getBottomButtonText,
    meIsLoading,
    modalTitle,
    modalDescription,
    invitationCode,
    showAppUpdateModal,
    setShowAppUpdateModal,
  } = MembershipIntroContainer.useContainer()
  const bodyImageUrl = `${S3_HOST_URL}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/membership_intro_body-25-04-01-1.jpg`
  const footerImageUrl = `${S3_HOST_URL}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/membership_intro_footer-25-04-01-1.jpg`
  const appUpdateWords = '최신 버전으로 업데이트 후\n초대 받은 링크를 통해 다시 접속\n해주세요.'.split('\n')

  useEffect(() => {
    if (isInAppFlag) {
      appBridgeProvider((bridge: Bridge) => {
        if (isAbleStatusBarFontChange) {
          bridge.statusBarColorWhite({ isWhite: false })
        }
        bridge.iosBounceToggle({ isShow: true })
      })
    }
  }, [isInAppFlag])

  useEffect(() => {
    if (showWarning && showWarningRef.current) {
      window.scrollTo({
        top: showWarningRef.current.offsetTop - showWarningRef.current.offsetHeight,
        behavior: 'smooth',
      })
    }
  }, [showWarning, showWarningRef.current])

  return (
    <StyledContainer>
      {meIsLoading ? (
        <Skeleton />
      ) : (
        <>
          <StyledImg src={bodyImageUrl} />
          <StyledAccordion onClick={toggleShowWarning} ref={showWarningRef}>
            <Text.Body1 fontColor={GlobalColorEnum.GrayWhite} fontWeight={FontWeightEnum.Bold}>
              유의사항
            </Text.Body1>
            <FlatIcon
              type={showWarning ? IconNameEnum.IcArrowUp : IconNameEnum.IcArrowDown}
              size={IconSizeEnum.Size24}
              color={GlobalColorEnum.Gray100}
            />
          </StyledAccordion>
          <StyledImg src={footerImageUrl} hide={!showWarning} />
          <StyledButtonContainer>
            <StyledBottomButton onClick={() => handleClickBottomButton()}>
              {getBottomButtonText()}
              <FitpetIcon type="ic-chevron-right" size={IconSizeEnum.Size24} color={GlobalColorEnum.Gray900} />
            </StyledBottomButton>
          </StyledButtonContainer>
        </>
      )}
      {visible && (
        <SimpleModal
          title={modalTitle}
          description={modalDescription}
          initialVisible
          onOk={modalOnOk}
          useCancel={false}
        />
      )}
      {showAppUpdateModal && (
        <SimpleModal
          title={
            invitationCode
              ? '패밀리 멤버십을 시작하려면\n앱 업데이트가 필요해요'
              : '핏펫플러스를 시작하려면\n앱 업데이트가 필요해요'
          }
          description={
            invitationCode ? (
              <StyledAppUpdateSubTitle>
                <Text.Body3>{appUpdateWords[0]}</Text.Body3>
                <StyledAppUpdateBottom>
                  <Text.Body3 fontColor={color.red500}>{appUpdateWords[1]}</Text.Body3>
                  <Text.Body3>{appUpdateWords[2]}</Text.Body3>
                </StyledAppUpdateBottom>
              </StyledAppUpdateSubTitle>
            ) : (
              '최신 버전으로 업데이트하고\n멤버십 혜택을 누려보세요.'
            )
          }
          okText="업데이트 하기"
          initialVisible
          onOk={() => {
            // 구버전 앱 업데이트
            if (isInAppFlag) {
              // 찜 목록 대응
              if (!isAppStorageDisabled) {
                appBridgeProvider((bridge: Bridge) => {
                  bridge
                    .storageSetItem('membershipInvitationCode', String(invitationCode))
                    .then((data: { status: boolean }) => {
                      if (data.status) {
                        appBridgeProvider((bridge: Bridge) => bridge.goAppUpdateAction())
                      } else {
                        console.error('Fail : updateNonLoginCartProductOptions')
                      }
                    })
                })
                return
              }
              appBridgeProvider((bridge: Bridge) => bridge.goAppUpdateAction())
            }
          }}
          onAfterClose={() => {
            setShowAppUpdateModal(false)
          }}
        />
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  padding-bottom: calc(8rem + env(safe-area-inset-bottom));
  background-color: ${({ theme: { color } }) => color.membershipIntro};
`

const StyledImg = styled.img<{ hide?: boolean }>`
  width: ${({ theme: { spacing } }) => spacing.per100};
  display: ${({ hide }) => (hide ? 'none' : 'initial')};
`

const StyledAccordion = styled.div`
  padding: 1.5rem 1.6rem 1.5rem;
  background-color: ${({ theme: { color } }) => color.membershipIntro};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledAppUpdateSubTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const StyledAppUpdateBottom = styled.div`
  display: flex;
`

const StyledButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  width: ${({ theme }) => theme.spacing.per100};
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  background-color: #ff8402;
  padding-bottom: env(safe-area-inset-bottom);
`

const StyledBottomButton = styled.button`
  width: ${({ theme: { spacing } }) => spacing.per100};
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  height: ${({ theme: { spacing } }) => spacing.size80};
  color: ${({ theme: { color } }) => color.gray900};
  gap: 0.5rem;
  background-color: #ff8402;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.bold};
  font-size: ${({ theme: { fontSize } }) => fontSize.h4};
  line-height: ${({ theme: { lineHeight } }) => lineHeight.h6};

  :disabled {
    background-color: ${({ theme: { color } }) => color.gray500};
  }
`

const PageMembershipIntro = () => {
  return (
    <>
      <Head>
        <meta property="og:type" content="website" key="type" />
        <meta property="og:title" content="핏펫플러스 멤버십 무료 초대장" key="title" />
        <meta property="og:image" content={MembershipImageUrl.OgImageUrl} key="image" />
        <meta property="og:description" content="패밀리 멤버 초대장이 도착했어요!" key="description" />
      </Head>
      <MembershipIntroContainer.Provider>
        <LayoutHeader />
        <PageMembershipIntroContent />
      </MembershipIntroContainer.Provider>
    </>
  )
}

export default PageMembershipIntro
